const BASE_URL = 'https://mongobyte-hc73.onrender.com/api/v1';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const discountApi = {
  // Create discount
  createDiscount: async (discountData) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(discountData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Create discount error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get all discounts with filtering
  getAllDiscounts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.restaurant) params.append('restaurant', filters.restaurant);

      const response = await fetch(`${BASE_URL}/discounts?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get discounts error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get discount by code
  getDiscountByCode: async (code) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/code/${code}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get discount by code error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Preview discount
  previewDiscount: async (previewData) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(previewData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Preview discount network error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Update discount
  updateDiscount: async (id, updateData) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Update discount error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Delete discount
  deleteDiscount: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Delete discount error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get discount usage statistics
  getDiscountUsage: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/${id}/usage`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get discount usage error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Validate discount (using preview endpoint)
  validateDiscount: async (validationData) => {
    try {
      // Transform the validation data to match the preview endpoint format
      const previewData = {
        discountCode: validationData.code || validationData.discountCode,
        orderAmount: validationData.orderAmount,
        restaurant: validationData.restaurantId || validationData.restaurant,
        deliveryFee: validationData.deliveryFee || 600
      };

      const response = await fetch(`${BASE_URL}/discounts/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(previewData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      const result = await response.json();
      // Transform the preview result to match expected validation response format
      if (result.success) {
        return { 
          success: true, 
          data: result.preview,
          preview: result.preview 
        };
      }
      
      return result;
    } catch (error) {
      console.error('Validate discount error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get discount suggestions
  getDiscountSuggestions: async (orderAmount, restaurantId, deliveryFee = 600) => {
    try {
      const params = new URLSearchParams({
        orderAmount: orderAmount.toString(),
        deliveryFee: deliveryFee.toString()
      });
      
      if (restaurantId) {
        params.append('restaurant', restaurantId);
      }

      const response = await fetch(`${BASE_URL}/discounts/suggestions?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get discount suggestions error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get personalized discounts
  getPersonalizedDiscounts: async () => {
    try {
      const response = await fetch(`${BASE_URL}/discounts/personalized`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get personalized discounts error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }
};

// Helper functions for discount calculations
export const discountHelpers = {
  calculateDiscountAmount: (orderAmount, discount) => {
    switch (discount.type) {
      case 'percentage':
        const percentageDiscount = (orderAmount * discount.value) / 100;
        return discount.maximumDiscountAmount 
          ? Math.min(percentageDiscount, discount.maximumDiscountAmount)
          : percentageDiscount;
      
      case 'fixed_amount':
        return Math.min(discount.value, orderAmount);
      
      case 'free_delivery':
        return discount.value || 600; // Default delivery fee
      
      default:
        return 0;
    }
  },

  formatDiscountType: (type) => {
    const types = {
      'percentage': 'Percentage',
      'fixed_amount': 'Fixed Amount',
      'free_delivery': 'Free Delivery'
    };
    return types[type] || type;
  },

  formatDiscountValue: (discount) => {
    switch (discount.type) {
      case 'percentage':
        return `${discount.value}%`;
      case 'fixed_amount':
        return `₦${discount.value}`;
      case 'free_delivery':
        return 'Free Delivery';
      default:
        return discount.value;
    }
  },

  getDiscountStatus: (discount) => {
    const now = new Date();
    const validFrom = new Date(discount.validFrom);
    const validUntil = new Date(discount.validUntil);

    if (!discount.isActive) return 'inactive';
    if (now < validFrom) return 'scheduled';
    if (now > validUntil) return 'expired';
    if (discount.usageLimit && discount.usageCount >= discount.usageLimit) return 'exhausted';
    
    return 'active';
  },

  getStatusColor: (status) => {
    const colors = {
      'active': 'text-green-600 bg-green-100',
      'inactive': 'text-gray-600 bg-gray-100',
      'expired': 'text-red-600 bg-red-100',
      'scheduled': 'text-primary-600 bg-primary-100',
      'exhausted': 'text-orange-600 bg-orange-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  }
};